import React, { useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  History,
  Info,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  Undo2,
  Users,
  X,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { calibrationRemediationService } from '@/services/api/calibrationRemediationService'
import type {
  StrandedMember, StrandedCause, RemediationAction, WorklistAction, RemediateResponse, LedgerEntry,
} from '@/services/api/calibrationRemediationService'
import { useReviewCycleConfig } from '@/hooks/useReviewCycleConfig'
import { getCurrentCalibrationPeriod } from '@/lib/reviewPeriod'

/**
 * Admin > Calibration fixes — read-only worklist (Phase 1).
 *
 * Answers "who has a completed check-in but never made it into a calibration
 * session, and why" without an engineer running a script. Applying the fix
 * lands in Phase 2; until then every row is advisory.
 *
 * Grouped by People Leader on purpose: the backlog is lumpy (one PL can account
 * for 15 of 40), and the fix is a conversation with that PL, not with 40 people.
 */

const RATING_LABELS: Record<string, string> = {
  exceptional: 'Exceptional',
  full: 'Full',
  'not-yet-full': 'Not Yet Full',
  weak: 'Weak',
  tstr: 'TSTR',
}

// Same palette as the calibration workspace, so a rating reads the same
// wherever it appears.
const ratingClass = (rating: string): string => {
  switch (rating) {
    case 'exceptional': return 'bg-purple-100 text-purple-800 border-purple-200'
    case 'full': return 'bg-green-100 text-green-800 border-green-200'
    case 'not-yet-full': return 'bg-amber-100 text-amber-800 border-amber-200'
    case 'weak': return 'bg-red-100 text-red-800 border-red-200'
    case 'tstr': return 'bg-slate-100 text-slate-700 border-slate-300'
    default: return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

const CAUSE_LABELS: Record<StrandedCause, { label: string; help: string }> = {
  late_check_in: {
    label: 'Check-in finished late',
    help: 'The check-in was completed after the session report was generated, so it was never in the grid.',
  },
  partial_submission: {
    label: 'Left out of the submission',
    help: 'Their People Leader submitted some of the team but not this person.',
  },
  never_submitted: {
    label: 'Nothing submitted',
    help: 'This People Leader has no submissions in the session at all.',
  },
}

const SESSION_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  'pre-calibration': 'Collecting submissions',
  'report-generation': 'Generating report',
  'in-progress': 'In progress',
  completed: 'Closed',
}

const RESOLUTION_HELP: Record<string, string> = {
  submissions: 'Matched to the session their other submissions are in.',
  roster: 'Matched to the session they hold a People Leader seat in.',
  'one-up': "Matched to their One-Up's session for this cycle.",
  ambiguous: 'This People Leader has submissions in more than one session.',
  none: 'No session found for this People Leader.',
}

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

/** What a pending fix looks like while the operator is confirming it. */
interface PendingFix {
  managerId: string
  managerName: string
  /** Empty for an offline capture — the session doesn't exist yet. */
  sessionId: string
  action: WorklistAction
  members: StrandedMember[]
  /** 'offline' creates the session first, for a PL who never had one. */
  mode: 'fix' | 'offline'
}

/** Ratings HR can set, in the calibration vocabulary the API expects. */
const RATING_CHOICES = ['exceptional', 'full', 'not-yet-full', 'weak', 'tstr'] as const

const ACTION_COPY: Record<WorklistAction, { verb: string; blurb: string }> = {
  confirm: {
    verb: 'Add to calibration',
    blurb: 'They join their People Leader’s calibration list with the rating from their own check-in. The chair still calibrates them.',
  },
  publish: {
    verb: 'Publish rating',
    blurb: 'The session has closed, so the rating from their check-in is published onto it directly — exactly as the session would have done.',
  },
}

const CalibrationFixesAdmin: React.FC = () => {
  const cycleConfig = useReviewCycleConfig()
  const queryClient = useQueryClient()
  const currentYear = new Date().getFullYear()

  const [reviewType, setReviewType] = useState<'mid-year' | 'year-end'>(
    () => getCurrentCalibrationPeriod(new Date(), cycleConfig)
  )
  const [reviewYear, setReviewYear] = useState<number>(currentYear)
  const [hideBlocked, setHideBlocked] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [actionFilter, setActionFilter] = useState<'all' | WorklistAction>('all')
  const [causeFilter, setCauseFilter] = useState<'all' | StrandedCause>('all')
  const [ratingFilter, setRatingFilter] = useState<string>('all')
  // People whose PL simply hasn't submitted yet. Hidden by default so the list
  // stays things to act on — but they were previously impossible to see at all,
  // which made a People Leader who had done nothing invisible.
  const [showWaiting, setShowWaiting] = useState(false)

  // Confirmation flow: pending → preview (dry-run) → applied.
  const [pending, setPending] = useState<PendingFix | null>(null)
  const [reason, setReason] = useState('')
  const [preview, setPreview] = useState<RemediateResponse | null>(null)
  const [outcome, setOutcome] = useState<RemediateResponse | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  // employee_id -> agreed rating, when the calibration moved someone off the
  // value in their check-in.
  const [overrides, setOverrides] = useState<Record<string, string>>({})

  // Undo. The banner and the publish dialog both promise this, so it has to be
  // reachable from the page — not just from the API.
  const [undoTarget, setUndoTarget] = useState<LedgerEntry | null>(null)
  const [undoReason, setUndoReason] = useState('')
  const [undoFailure, setUndoFailure] = useState<string | null>(null)
  const [showLedger, setShowLedger] = useState(false)

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['calibration-stranded', reviewType, reviewYear, showWaiting],
    queryFn: () => calibrationRemediationService.listStranded({
      reviewType, reviewYear, includeAwaiting: showWaiting,
    }),
    staleTime: 60 * 1000,
  })

  const closeDialog = () => {
    setPending(null); setReason(''); setPreview(null); setOutcome(null)
    setFailure(null); setOverrides({})
  }

  /** Only the ones actually moved get sent. */
  const changedRatings = (fix: PendingFix): Record<string, string> =>
    Object.fromEntries(
      fix.members
        .filter(m => overrides[m.employee_id] && overrides[m.employee_id] !== m.rating)
        .map(m => [m.employee_id, overrides[m.employee_id]])
    )

  /** Dry-run: ask the server exactly what it would do, and show that verbatim. */
  const runFix = (fix: PendingFix, dryRun: boolean) => {
    const ratings = changedRatings(fix)
    return fix.mode === 'offline'
      ? calibrationRemediationService.recordOffline({
          managerId: fix.managerId,
          reviewType, reviewYear, reason,
          members: fix.members.map(m => ({
            employee_id: m.employee_id,
            ...(ratings[m.employee_id] ? { rating: ratings[m.employee_id] } : {}),
          })),
        }, dryRun)
      : calibrationRemediationService.remediate(
          fix.sessionId, fix.action,
          { employeeIds: fix.members.map(m => m.employee_id), reason, reviewType, reviewYear, ratings },
          dryRun,
        )
  }

  const previewMutation = useMutation({
    mutationFn: (fix: PendingFix) => runFix(fix, true),
    onSuccess: res => { setPreview(res); setFailure(null) },
    onError: (e: Error) => setFailure(e.message),
  })

  const applyMutation = useMutation({
    mutationFn: (fix: PendingFix) => runFix(fix, false),
    onSuccess: res => {
      setOutcome(res); setPreview(null); setFailure(null)
      queryClient.invalidateQueries({ queryKey: ['calibration-stranded', reviewType, reviewYear, showWaiting] })
      queryClient.invalidateQueries({ queryKey: ['calibration-remediations'] })
    },
    onError: (e: Error) => setFailure(e.message),
  })

  const { data: ledger } = useQuery({
    queryKey: ['calibration-remediations'],
    queryFn: () => calibrationRemediationService.listRemediations({ limit: 50 }),
    staleTime: 30 * 1000,
  })

  // The cycle defaults to whichever period we're in, which for the months right
  // after the year-end boundary means an empty tab while the real backlog sits
  // in the other cycle. Only asked when this one comes back empty — so it costs
  // nothing in the normal case and answers "why is this blank?" in the odd one.
  const otherType: 'mid-year' | 'year-end' = reviewType === 'mid-year' ? 'year-end' : 'mid-year'
  const { data: otherCycle } = useQuery({
    queryKey: ['calibration-stranded', otherType, reviewYear],
    queryFn: () => calibrationRemediationService.listStranded({ reviewType: otherType, reviewYear }),
    enabled: !!data && !data.pairs_unavailable && data.summary.stranded === 0,
    staleTime: 60 * 1000,
  })

  const undoMutation = useMutation({
    mutationFn: (entry: LedgerEntry) =>
      calibrationRemediationService.undoRemediation(entry.id, undoReason),
    onSuccess: () => {
      setUndoTarget(null); setUndoReason(''); setUndoFailure(null)
      queryClient.invalidateQueries({ queryKey: ['calibration-stranded', reviewType, reviewYear, showWaiting] })
      queryClient.invalidateQueries({ queryKey: ['calibration-remediations'] })
    },
    onError: (e: Error) => setUndoFailure(e.message),
  })

  const busy = previewMutation.isPending || applyMutation.isPending

  const startFix = (managerId: string, managerName: string, members: StrandedMember[]) => {
    const fixable = members.filter(
      m => !m.awaiting_submitter && m.remediable && m.resolved_session.session_id
    )
    if (!fixable.length) return
    setPending({
      managerId,
      managerName,
      sessionId: fixable[0].resolved_session.session_id as string,
      // Server-derived, never a user choice — it follows the session's status.
      action: fixable[0].available_actions[0] as WorklistAction,
      members: fixable,
      mode: 'fix',
    })
    setReason(''); setPreview(null); setOutcome(null); setFailure(null)
    setOverrides(Object.fromEntries(fixable.map(m => [m.employee_id, m.rating])))
  }

  /** For a People Leader with no session at all: capture what happened offline. */
  const startOffline = (managerId: string, managerName: string, members: StrandedMember[]) => {
    const eligible = members.filter(
      m => !m.awaiting_submitter && m.blockers.length > 0
        && m.blockers.every(b => b.code === 'no_session')
    )
    if (!eligible.length) return
    setPending({
      managerId, managerName, sessionId: '', action: 'publish',
      members: eligible, mode: 'offline',
    })
    setReason(''); setPreview(null); setOutcome(null); setFailure(null)
    setOverrides(Object.fromEntries(eligible.map(m => [m.employee_id, m.rating])))
  }

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (data?.rows ?? []).filter(r => {
      if (hideBlocked && !r.remediable) return false
      if (actionFilter !== 'all' && r.available_actions[0] !== actionFilter) return false
      if (causeFilter !== 'all' && r.cause !== causeFilter) return false
      if (ratingFilter !== 'all' && r.rating !== ratingFilter) return false
      if (!q) return true
      // Personnel number matters as much as name here — HR works from PNs.
      return [r.employee_name, r.employee_id, r.job_title, r.manager_name, r.manager_id,
        ...(r.now_reports_to ?? []).flatMap(x => [x.name, x.employee_id])]
        .some(v => (v || '').toLowerCase().includes(q))
    })
  }, [data?.rows, hideBlocked, search, actionFilter, causeFilter, ratingFilter])

  const filtered = rows.length !== (data?.rows.length ?? 0)
  const clearFilters = () => {
    setSearch(''); setActionFilter('all'); setCauseFilter('all')
    setRatingFilter('all'); setHideBlocked(false)
  }

  // Preserve the server's ordering (People Leader, then member) while grouping.
  const groups = useMemo(() => {
    const byManager = new Map<string, {
      managerId: string; managerName: string; chair: string | null; members: StrandedMember[]
    }>()
    for (const r of rows) {
      const g = byManager.get(r.manager_id)
      if (g) g.members.push(r)
      else byManager.set(r.manager_id, {
        managerId: r.manager_id,
        managerName: r.manager_name,
        // Who this People Leader presents to — the session's chair if it exists,
        // otherwise the One-Up who would chair it.
        chair: r.resolved_session.facilitator_name || r.expected_chair_name || null,
        members: [r],
      })
    }
    return [...byManager.values()]
  }, [rows])

  // Ratings actually present, so the filter never offers a dead option.
  const ratingsPresent = useMemo(
    () => [...new Set((data?.rows ?? []).map(r => r.rating))].sort(),
    [data?.rows]
  )

  const toggle = (managerId: string) =>
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(managerId)) next.delete(managerId)
      else next.add(managerId)
      return next
    })

  const allCollapsed = groups.length > 0 && groups.every(g => collapsed.has(g.managerId))
  const toggleAll = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(groups.map(g => g.managerId)))

  const years = useMemo(
    () => [currentYear + 1, currentYear, currentYear - 1, currentYear - 2],
    [currentYear]
  )

  return (
    <div className="space-y-4">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Calibration fixes</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Team members whose check-in is finished and rated, but who never made it into a
            calibration session. Their rating is sitting unpublished.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={reviewType} onValueChange={v => setReviewType(v as 'mid-year' | 'year-end')}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mid-year">Mid-Year</SelectItem>
              <SelectItem value="year-end">Year-End</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(reviewYear)} onValueChange={v => setReviewYear(Number(v))}>
            <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Fixes use the rating the People Leader already recorded in the check-in — nothing is
          invented. You’ll see exactly what will happen before anything is saved, and every fix
          can be undone.
        </p>
      </div>

      {/* ── Directory unavailable ──────────────────────────────────────── */}
      {data?.pairs_unavailable && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Can&apos;t check right now</p>
            <p className="mt-0.5">
              {data.message ||
                "Couldn't reach the employee directory, so this list is incomplete. Try again shortly — don't assume all is well."}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Couldn&apos;t load the list. {(error as Error).message}</p>
        </div>
      )}

      {/* ── Summary ────────────────────────────────────────────────────── */}
      {data && !data.pairs_unavailable && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Card className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Stranded</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{data.summary.stranded}</p>
            <p className="mt-0.5 text-xs text-gray-500">rated, but not in any session</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">People Leaders</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{data.summary.people_leaders}</p>
            <p className="mt-0.5 text-xs text-gray-500">to follow up with</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Need a decision</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{data.summary.blocked}</p>
            <p className="mt-0.5 text-xs text-gray-500">can&apos;t be fixed automatically</p>
          </Card>
        </div>
      )}

      {/* What this list leaves out — stated, and now reachable. */}
      {!!data?.summary.awaiting_submitter && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5">
          <p className="text-sm text-gray-700">
            {showWaiting ? 'Showing' : 'Not counted above:'}{' '}
            <span className="font-medium">{data.summary.awaiting_submitter}</span>{' '}
            {data.summary.awaiting_submitter === 1 ? 'person' : 'people'} whose People Leader
            hasn&apos;t submitted them yet, across{' '}
            {data.summary.awaiting_people_leaders}{' '}
            {data.summary.awaiting_people_leaders === 1 ? 'People Leader' : 'People Leaders'}.
            They aren&apos;t stranded — the fix is a nudge, not a correction.
          </p>
          <Button variant="outline" size="sm" onClick={() => setShowWaiting(v => !v)}>
            {showWaiting ? 'Hide them' : 'Show them'}
          </Button>
        </div>
      )}

      {/* ── Search & filters ───────────────────────────────────────────── */}
      {!!data?.rows.length && (
        <Card className="space-y-3 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[240px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name, personnel number, job title or People Leader"
                className="pl-8 pr-8"
                aria-label="Search stranded team members"
              />
              {!!search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <Select value={actionFilter} onValueChange={v => setActionFilter(v as typeof actionFilter)}>
              <SelectTrigger className="w-[168px]" aria-label="Filter by fix">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any fix</SelectItem>
                <SelectItem value="publish">Publish rating</SelectItem>
                <SelectItem value="confirm">Add to calibration</SelectItem>
              </SelectContent>
            </Select>

            <Select value={causeFilter} onValueChange={v => setCauseFilter(v as typeof causeFilter)}>
              <SelectTrigger className="w-[190px]" aria-label="Filter by reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any reason</SelectItem>
                <SelectItem value="late_check_in">{CAUSE_LABELS.late_check_in.label}</SelectItem>
                <SelectItem value="partial_submission">{CAUSE_LABELS.partial_submission.label}</SelectItem>
                <SelectItem value="never_submitted">{CAUSE_LABELS.never_submitted.label}</SelectItem>
              </SelectContent>
            </Select>

            {ratingsPresent.length > 1 && (
              <Select value={ratingFilter} onValueChange={setRatingFilter}>
                <SelectTrigger className="w-[150px]" aria-label="Filter by rating">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any rating</SelectItem>
                  {ratingsPresent.map(r => (
                    <SelectItem key={r} value={r}>{RATING_LABELS[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Switch id="hide-blocked" checked={hideBlocked} onCheckedChange={setHideBlocked} />
              <Label htmlFor="hide-blocked" className="cursor-pointer text-sm text-gray-700">
                Only show the ones that can be fixed automatically
              </Label>
            </div>

            <div className="flex items-center gap-3">
              {/* Filters change what the buttons below will act on, so the count
                  has to be visible — the summary cards above are unfiltered. */}
              {filtered && (
                <span className="text-sm text-gray-600">
                  Showing <span className="font-medium">{rows.length}</span> of {data.rows.length}
                </span>
              )}
              {filtered && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>
              )}
              {groups.length > 1 && (
                <Button variant="outline" size="sm" onClick={toggleAll}>
                  {allCollapsed ? 'Expand all' : 'Collapse all'}
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ── Worklist ───────────────────────────────────────────────────── */}
      {isLoading ? (
        <Card className="flex items-center justify-center gap-2 p-12 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking every People Leader&apos;s session…
        </Card>
      ) : !groups.length ? (
        <Card className="flex flex-col items-center gap-2 p-12 text-center">
          <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          <p className="font-medium text-gray-900">
            {data?.rows.length ? 'Nothing matches this filter' : 'Nobody is stranded'}
          </p>
          <p className="max-w-md text-sm text-gray-600">
            {data?.rows.length
              ? `Nothing here matches what you've filtered to — there ${data.rows.length === 1 ? 'is 1 person' : `are ${data.rows.length} people`} in this cycle.`
              : `Every rated ${reviewType === 'mid-year' ? 'mid-year' : 'year-end'} check-in for ${reviewYear} is accounted for in a calibration session.`}
          </p>
          {!!data?.rows.length && (
            <Button variant="outline" size="sm" onClick={clearFilters}>Clear filters</Button>
          )}

          {/* An empty cycle usually means you're looking at the wrong one — the
              tab opens on the current period, which is empty for months after
              the year-end boundary. Say where the work actually is. */}
          {!data?.rows.length && !!otherCycle?.summary.stranded && (
            <div className="mt-3 flex flex-col items-center gap-2">
              <p className="max-w-md text-sm text-gray-700">
                {otherType === 'mid-year' ? 'Mid-Year' : 'Year-End'} {reviewYear} has{' '}
                <span className="font-medium">{otherCycle.summary.stranded}</span> waiting across{' '}
                {otherCycle.summary.people_leaders}{' '}
                {otherCycle.summary.people_leaders === 1 ? 'People Leader' : 'People Leaders'}.
              </p>
              <Button variant="outline" size="sm" onClick={() => setReviewType(otherType)}>
                Show {otherType === 'mid-year' ? 'Mid-Year' : 'Year-End'} {reviewYear}
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {groups.map(group => {
            const isCollapsed = collapsed.has(group.managerId)
            const blockedCount = group.members.filter(m => !m.remediable && !m.awaiting_submitter).length
            const sess = group.members[0]?.resolved_session
            return (
              <Card key={group.managerId} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggle(group.managerId)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                    )}
                    <Users className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="truncate font-medium text-gray-900">{group.managerName}</span>
                    <span className="shrink-0 text-sm text-gray-500">({group.managerId})</span>
                    {!!group.chair && (
                      <span className="shrink-0 truncate text-sm text-gray-500">
                        presents to <span className="text-gray-700">{group.chair}</span>
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {sess?.status && (
                      <Badge variant="outline" className="bg-gray-50 text-gray-700" title={RESOLUTION_HELP[sess.rule]}>
                        Session: {SESSION_STATUS_LABELS[sess.status] || sess.status}
                      </Badge>
                    )}
                    {group.members.some(m => m.awaiting_submitter) && (
                      <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-800">
                        {group.members.filter(m => m.awaiting_submitter).length} not submitted yet
                      </Badge>
                    )}
                    {blockedCount > 0 && (
                      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
                        <Lock className="mr-1 h-3 w-3" />
                        {blockedCount} need{blockedCount === 1 ? 's' : ''} a decision
                      </Badge>
                    )}
                    <Badge variant="outline" className="bg-gray-50 text-gray-700">
                      {group.members.length} {group.members.length === 1 ? 'person' : 'people'}
                    </Badge>
                  </div>
                </button>

                {/* Action bar. The verb comes from the server's available_actions
                    — confirm vs publish follows the session status, never a choice. */}
                {(() => {
                  const waiting = group.members.filter(m => m.awaiting_submitter)
                  const fixable = group.members.filter(
                    m => !m.awaiting_submitter && m.remediable && m.resolved_session.session_id
                  )
                  // A People Leader with no session at all can't be fixed the
                  // normal way — but their calibration may simply have happened
                  // off-platform, and that record can still be captured.
                  const offlineable = group.members.filter(
                    m => !m.awaiting_submitter && m.blockers.length > 0
                      && m.blockers.every(b => b.code === 'no_session')
                  )
                  if (!fixable.length && !offlineable.length && !waiting.length) return null
                  const action = fixable[0]?.available_actions[0] as WorklistAction | undefined
                  return (
                    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/60 px-4 py-2">
                      <p className="text-xs text-gray-600">
                        {fixable.length
                          ? `${fixable.length} of ${group.members.length} can be fixed automatically.`
                          : offlineable.length
                            ? 'No calibration session covers this People Leader.'
                            : `Waiting on ${group.managerName} to submit — nothing to correct here.`}
                      </p>
                      <div className="flex items-center gap-2">
                        {!!offlineable.length && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startOffline(group.managerId, group.managerName, group.members)}
                          >
                            <ClipboardCheck className="mr-2 h-4 w-4" />
                            Record offline calibration ({offlineable.length})
                          </Button>
                        )}
                        {!!fixable.length && action && (
                          <Button
                            size="sm"
                            onClick={() => startFix(group.managerId, group.managerName, group.members)}
                          >
                            {ACTION_COPY[action].verb} ({fixable.length})
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })()}

                {!isCollapsed && (
                  <div className="border-t border-gray-200">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Team member</TableHead>
                          <TableHead>Rating</TableHead>
                          <TableHead>Check-in done</TableHead>
                          <TableHead>Why they&apos;re missing</TableHead>
                          <TableHead>What would fix it</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.members.map(m => (
                          <TableRow
                            key={m.employee_id}
                            className={
                              m.awaiting_submitter ? 'bg-blue-50/40'
                                : m.remediable ? undefined : 'bg-amber-50/40'
                            }
                          >
                            <TableCell className="align-top">
                              <div className="font-medium text-gray-900">{m.employee_name || m.employee_id}</div>
                              <div className="text-xs text-gray-500">
                                {m.employee_id}
                                {m.job_title ? ` · ${m.job_title}` : ''}
                              </div>
                              {m.moved_since_checkin && (
                                // Listed under who did the check-in, not who they report to now.
                                <div className="mt-1 text-xs text-gray-600">
                                  {m.now_reports_to?.length
                                    ? `Now reports to ${m.now_reports_to.map(x => x.name).join(' and ')}. `
                                    : 'Moved since their check-in. '}
                                  {m.manager_name} did their check-in, so they&apos;re presented here.
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="align-top">
                              <Badge variant="outline" className={ratingClass(m.rating)}>
                                {RATING_LABELS[m.rating] || m.rating}
                              </Badge>
                              <div className="mt-1 text-xs text-gray-500">
                                {m.kpa_count} KPA{m.kpa_count === 1 ? '' : 's'} rated
                              </div>
                            </TableCell>
                            <TableCell className="align-top text-sm text-gray-700">
                              {formatDate(m.completed_date)}
                            </TableCell>
                            <TableCell className="align-top">
                              <span className="text-sm text-gray-700" title={CAUSE_LABELS[m.cause]?.help}>
                                {CAUSE_LABELS[m.cause]?.label || m.cause}
                              </span>
                            </TableCell>
                            <TableCell className="max-w-md align-top">
                              {m.awaiting_submitter ? (
                                <p className="text-sm text-blue-900">
                                  Waiting on {m.manager_name} to submit them
                                  {m.resolved_session.facilitator_name
                                    ? ` to ${m.resolved_session.facilitator_name}`
                                    : ''}. That session is
                                  {' '}{SESSION_STATUS_LABELS[m.resolved_session.status || '']
                                    ?.toLowerCase() || m.resolved_session.status || 'not started'}.
                                </p>
                              ) : m.remediable ? (
                                <p className="text-sm text-gray-600">{m.what_happens}</p>
                              ) : (
                                <ul className="space-y-1">
                                  {m.blockers.map(b => (
                                    <li key={b.code} className="flex items-start gap-1.5 text-sm text-amber-900">
                                      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                      <span>{b.message}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {/* ── Recent fixes + undo ──────────────────────────────────────────── */}
      {!!ledger?.rows.length && (
        <Card className="overflow-hidden">
          <button
            type="button"
            onClick={() => setShowLedger(v => !v)}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
          >
            <div className="flex items-center gap-2">
              {showLedger ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
              )}
              <History className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="font-medium text-gray-900">Recent fixes</span>
            </div>
            <Badge variant="outline" className="bg-gray-50 text-gray-700">
              {ledger.total} applied
            </Badge>
          </button>

          {showLedger && (
            <div className="border-t border-gray-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team member</TableHead>
                    <TableHead>What was done</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead className="text-right">&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledger.rows.map(e => (
                    <TableRow key={e.id} className={e.is_undone ? 'text-gray-500' : undefined}>
                      <TableCell className="align-top">
                        <div className="font-medium text-gray-900">
                          {e.employee_name || e.employee_id}
                        </div>
                        <div className="text-xs text-gray-500">
                          {e.employee_id}{e.manager_name ? ` · ${e.manager_name}` : ''}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-sm align-top">
                        <div className="text-sm text-gray-700">
                          {e.action === 'publish' ? 'Rating published' : 'Added to calibration'}
                          {e.is_undone && <span className="ml-1 text-gray-500">· undone</span>}
                        </div>
                        <div className="text-xs text-gray-500">{e.reason}</div>
                      </TableCell>
                      <TableCell className="align-top text-sm text-gray-700">
                        <div>{e.applied_by}</div>
                        <div className="text-xs text-gray-500">{formatDate(e.applied_at)}</div>
                      </TableCell>
                      <TableCell className="align-top text-right">
                        {!e.is_undone && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setUndoTarget(e); setUndoReason(''); setUndoFailure(null) }}
                          >
                            <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                            Undo
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}

      {/* ── Undo dialog ──────────────────────────────────────────────────── */}
      <Dialog
        open={!!undoTarget}
        onOpenChange={open => { if (!open && !undoMutation.isPending) { setUndoTarget(null); setUndoFailure(null) } }}
      >
        <DialogContent>
          {undoTarget && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Undo — {undoTarget.employee_name || undoTarget.employee_id}
                </DialogTitle>
                <DialogDescription>
                  {undoTarget.action === 'publish'
                    ? 'The published rating is removed from their check-in and the records are put back exactly as they were.'
                    : 'Their submission is removed from the calibration session.'}
                </DialogDescription>
              </DialogHeader>

              {undoTarget.action === 'publish' && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    This restores the records, but the rating was visible to the employee while it
                    was published — that part can’t be reversed.
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="undo-reason" className="text-sm">Why are you undoing this?</Label>
                <Textarea
                  id="undo-reason"
                  rows={2}
                  value={undoReason}
                  onChange={e => setUndoReason(e.target.value)}
                  placeholder="e.g. Applied to the wrong person."
                />
              </div>

              {undoFailure && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{undoFailure}</p>
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { setUndoTarget(null); setUndoFailure(null) }}
                  disabled={undoMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => undoMutation.mutate(undoTarget)}
                  disabled={undoMutation.isPending || undoReason.trim().length < 5}
                >
                  {undoMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Undo this fix
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Confirm dialog: reason → preview → apply ─────────────────────── */}
      <Dialog open={!!pending} onOpenChange={open => { if (!open && !busy) closeDialog() }}>
        <DialogContent className="max-w-2xl">
          {pending && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {outcome
                    ? 'Done'
                    : pending.mode === 'offline'
                      ? `Record offline calibration — ${pending.managerName}`
                      : `${ACTION_COPY[pending.action].verb} — ${pending.managerName}`}
                </DialogTitle>
                <DialogDescription>
                  {outcome
                    ? `${outcome.applied} of ${outcome.total} applied.`
                    : pending.mode === 'offline'
                      ? 'Creates the calibration record that should exist: a session chaired by this People Leader’s One-Up, a submission per person, and the agreed rating published onto their check-in.'
                      : ACTION_COPY[pending.action].blurb}
                </DialogDescription>
              </DialogHeader>

              {/* Publishing is immediately visible to the employee, and undo
                  restores rows but cannot un-see. Say so before, not after. */}
              {!outcome && pending.mode === 'offline' && (
                <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    Use this only when the calibration genuinely happened — this writes it
                    up as a completed session. Adjust any rating the meeting moved; the
                    People Leader’s original assessment is kept alongside it.
                  </p>
                </div>
              )}

              {!outcome && pending.mode !== 'offline' && pending.action === 'publish' && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    The rating becomes visible to the employee straight away. You can undo this,
                    but that restores the records — it can’t un-show what was seen.
                  </p>
                </div>
              )}

              <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team member</TableHead>
                      <TableHead>From check-in</TableHead>
                      <TableHead>Agreed rating</TableHead>
                      <TableHead>Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(outcome?.results ?? preview?.results ?? pending.members.map(m => ({
                      employee_id: m.employee_id, employee_name: m.employee_name,
                      rating: m.rating, applied: true, reason: null,
                    }))).map(r => (
                      <TableRow key={r.employee_id}>
                        <TableCell>
                          <div className="text-sm font-medium text-gray-900">
                            {r.employee_name || r.employee_id}
                          </div>
                          <div className="text-xs text-gray-500">{r.employee_id}</div>
                        </TableCell>
                        <TableCell>
                          {r.rating && (
                            <Badge variant="outline" className={ratingClass(r.rating)}>
                              {RATING_LABELS[r.rating] || r.rating}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Pre-filled from the check-in. Changing it records a
                              calibration outcome; the check-in value is kept as
                              what the People Leader actually proposed. */}
                          {outcome ? (
                            <Badge
                              variant="outline"
                              className={ratingClass(overrides[r.employee_id] || r.rating || '')}
                            >
                              {RATING_LABELS[overrides[r.employee_id] || r.rating || ''] ||
                                overrides[r.employee_id] || r.rating}
                            </Badge>
                          ) : (
                            <Select
                              value={overrides[r.employee_id] ?? r.rating ?? ''}
                              onValueChange={v => {
                                setOverrides(prev => ({ ...prev, [r.employee_id]: v }))
                                setPreview(null)
                              }}
                            >
                              <SelectTrigger className="h-8 w-[150px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {RATING_CHOICES.map(v => (
                                  <SelectItem key={v} value={v}>
                                    {RATING_LABELS[v] || v}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {r.applied ? (
                            <span className="text-emerald-700">
                              {outcome ? 'Applied' : preview ? 'Will apply' : 'Ready'}
                            </span>
                          ) : (
                            <span className="text-amber-800">{r.reason}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {!outcome && (
                <div className="space-y-1.5">
                  <Label htmlFor="fix-reason" className="text-sm">
                    Why are you doing this? <span className="text-gray-500">(recorded against the fix)</span>
                  </Label>
                  <Textarea
                    id="fix-reason"
                    rows={2}
                    value={reason}
                    onChange={e => { setReason(e.target.value); setPreview(null) }}
                    placeholder="e.g. Check-in was completed after the session closed — confirmed with the People Leader."
                  />
                </div>
              )}

              {failure && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{failure}</p>
                </div>
              )}

              <DialogFooter>
                {outcome ? (
                  <Button onClick={closeDialog}>Close</Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={closeDialog} disabled={busy}>Cancel</Button>
                    {preview ? (
                      <Button
                        onClick={() => applyMutation.mutate(pending)}
                        disabled={busy || preview.applied === 0}
                      >
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Apply to {preview.applied} {preview.applied === 1 ? 'person' : 'people'}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => previewMutation.mutate(pending)}
                        disabled={busy || reason.trim().length < 5}
                      >
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Preview
                      </Button>
                    )}
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CalibrationFixesAdmin
